import * as React       from 'react';
import CircularProgress from "@mui/material/CircularProgress";
import Dialog           from '@mui/material/Dialog';
import Stack            from "@mui/material/Stack";
import Typography       from '@mui/material/Typography'
import Menu             from '@mui/material/Menu';
import MenuItem         from '@mui/material/MenuItem';
import MoreVert         from '@mui/icons-material/MoreVert';
import {
    DataGrid,
    GridActionsCellItem,
    GridColumns,
    GridRowParams
}                       from "@mui/x-data-grid"
import {
    useQueryClient,
    UseQueryResult
}                       from "react-query";
import {
    STUDENTS_TABLE_EMPTY,
    STUDENTS_TABLE_NAME,
    STUDENTS_TABLE_PROGRAM,
    STUDENTS_TABLE_DATA_ERROR,
    CLASS_TABLE_QR_CODE_TITLE,
    CLASS_TABLE_MORE_ACTIONS,
    CLASS_TABLE_REMOVE,
    CLASS_TABLE_EDIT,
    CLASSES_TABLE_NUM,
    EDIT_STUDENT_TITLE,
    DELETE_STUDENT_TITLE,
}                           from 'config/config';
import { useProgramCatalog, programLabel } from 'hooks/useProgramCatalog';
import IProfile             from "models/IProfile";
import PopupWithCloseButton from 'ui/components/classes-components/PopupWithCloseButton';
import EditStudent          from 'ui/components/classes-components/EditStudent';
import DeleteStudent        from 'ui/components/classes-components/DeleteStudent';
import {
    ReactComponent as QrCode
}                           from 'icons/qrCode.svg';

export default function ClassTable(
    props: {
        profiles: UseQueryResult<IProfile[]>,
        classSource: 'teacher' | `provider:${string}`,
        classId?: string,
        showQrCode: (profileId: string) => void
    }
) {
    const [menuOpen, setMenuOpen] = React.useState(false);
    const [menuRef, setMenuRef] = React.useState<null | HTMLElement>(null);
    const [popupOpen, setPopupOpen] = React.useState(false);
    const [popupElement, setPopupElement] = React.useState<null | JSX.Element>(null);
    const [popupTitle, setPopupTitle] = React.useState('');
    const [selectedStudent, setSelectedStudent] = React.useState('');
    const catalog = useProgramCatalog();
    const closePopup = () => {
        setPopupOpen(false);
        setPopupTitle('');
        setPopupElement(null);
    };
    const queryClient = useQueryClient()
    const invalidate = () => {
        queryClient.invalidateQueries(`/school/classes/${props.classId}/members`);
    };
    const data_columns: GridColumns = [
        {   field: 'id',
            headerName: 'ID',
            editable: false,
            hide: true
        },
        {   field: 'num',
            headerName: CLASSES_TABLE_NUM,
            width: 56,
            editable: false,
            disableColumnMenu: true,
            hideSortIcons: true,
            align: 'center',
            headerAlign: 'center',
            cellClassName: 'NoPadding',
            headerClassName: 'NoPadding'
        },
        {   field: 'name',
            headerName: STUDENTS_TABLE_NAME,
            width: 220,
            editable: props.classSource === 'teacher', // Разрешаем редактирование только для классов с source === 'teacher'
            disableColumnMenu: true
        },
        {   field: 'program',
            headerName: STUDENTS_TABLE_PROGRAM,
            minWidth: 160,
            flex: 1,
            editable: false,
            disableColumnMenu: true
        },
        {   field: 'actions',
            type: 'actions',
            flex: 0.25,
            minWidth: 60,
            align: 'right',
            cellClassName: 'ActionsCellRight',
            hideSortIcons: true,
            disableColumnMenu: true,
            getActions: (params: GridRowParams) => {
                let actions = [
                    <GridActionsCellItem
                        sx={{mx: 0}}
                        icon={<QrCode title={CLASS_TABLE_QR_CODE_TITLE}/>}
                        onClick={() => {
                            props.showQrCode(params.id.toString())
                        }}
                        label={CLASS_TABLE_QR_CODE_TITLE}
                    />,
                ];
                if (props.classSource === 'teacher') {
                    // ⋮ menu sits to the left of the QR icon.
                    actions.unshift(
                        <GridActionsCellItem
                            sx={{mx: 0}}
                            icon={<MoreVert titleAccess={CLASS_TABLE_MORE_ACTIONS}/>}
                            onClick={(e) => {
                                setMenuRef(e.currentTarget);
                                setSelectedStudent(params.id.toString())
                                setMenuOpen(true);
                            }}
                            label={CLASS_TABLE_MORE_ACTIONS}
                        />
                    );
                }
                return actions;
            } 
        }
    ];
    if (props.profiles.isLoading) {
        return <CircularProgress sx={{alignSelf: 'center'}}/>
    } else if (props.profiles.isError || (
        props.profiles.data as unknown as {code: number})?.code >= 400
    ) {
        return (
            <Typography color='error'>
                {STUDENTS_TABLE_DATA_ERROR}
            </Typography>);
    };
    return(
        <>
            <DataGrid
                autoHeight
                sortingOrder={['desc', 'asc']}
                //checkboxSelection
                columns={data_columns}
                rows={(
                    props.profiles?.data
                    && props.profiles.data.filter(prof=>prof.profile_type==='student').sort(
                        (a, b)=>(a.first_name).localeCompare(b.first_name)
                    ).map((prof, idx)=>{
                    return {
                        id: prof.id,
                        num: idx + 1,
                        name: `${prof.first_name || ''}${prof.last_name ? ' ' + prof.last_name : ''}`,
                        program: programLabel(catalog, prof.program),
                    }
                })) || []}
                hideFooter={true}
                rowHeight={56}
                headerHeight={56}
                components={{
                    NoRowsOverlay: () => (
                        <Stack height="100%" alignItems="center" justifyContent="center">
                            {STUDENTS_TABLE_EMPTY}
                        </Stack>
                    )
                }}
            />
            {props.classSource === 'teacher' && (
            <Menu
                id="basic-menu"
                anchorEl={menuRef}
                open={menuOpen}
                onClose={()=>{
                    setMenuOpen(false);
                }}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                <MenuItem onClick={()=>{
                     setPopupElement(
                        <DeleteStudent
                            closePopup={closePopup}
                            classId={props.classId || ''}
                            studentId={selectedStudent}
                            studentData={props.profiles?.data?.filter(st=>st.id===selectedStudent)[0]}
                            invalidate={invalidate}
                        />
                    );
                    setPopupTitle(DELETE_STUDENT_TITLE);
                    setMenuOpen(false);
                    setPopupOpen(true);
                }}>
                    {CLASS_TABLE_REMOVE}
                </MenuItem>
                <MenuItem onClick={()=>{
                    setPopupElement(
                        <EditStudent
                            closePopup={closePopup}
                            classId={props.classId || ''}
                            studentId={selectedStudent}
                            studentData={props.profiles?.data?.filter(st=>st.id===selectedStudent)[0]}
                            invalidate={invalidate}
                        />
                    );
                    setPopupTitle(EDIT_STUDENT_TITLE);
                    setMenuOpen(false);
                    setPopupOpen(true);
                }}>
                    {CLASS_TABLE_EDIT}
                </MenuItem>
            </Menu>
            )}
            <Dialog
                open={popupOpen}
                onClose={closePopup}
            >
                <PopupWithCloseButton
                    closePopap={closePopup}
                    title={popupTitle}
                >
                    {popupElement || <></>}
                </PopupWithCloseButton>
            </Dialog>
        </>
    );
};